import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconWand } from '@tabler/icons-react'
import { isEmpty, isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'

import { toast } from '@/components/toasts/toast.helpers'
import {
  dismissOptimization,
  fetchLatestRun,
  pollOptimization,
  savePromptForOptimization,
  startOptimization,
} from '@/reducers/promptimizer/promptimizer.actions'
import {
  ACTIVE_STATUSES,
  POLLABLE_STATUSES,
  RUN_STATUS,
  resetRun,
  selectRun,
  setRun,
  setStatus,
} from '@/reducers/promptimizer/promptimizer.redux'

import { OptimizeRunPanel } from './OptimizeRunPanel'
import { OptimizeResult } from './OptimizeResult'
import { canReuseCriterionPrompt, matchesCriterion, usesFallback } from './optimize.helpers'

const POLL_INTERVAL_MS = 5000

// A stable empty list, so a store with no promptimizer slice (which is every existing test using `redux-mock-store`)
// doesn't hand `useSelector` a fresh array on every render and re-render forever.
const NO_DISMISSED = []

// Checked here as well as in the criterion schema, because Optimize acts on the current textarea value. A prompt
// with no placeholder fails silently: the transcript is never substituted and the run returns confident numbers
// about nothing.
const TRANSCRIPT_PLACEHOLDER = /\$transcript/

const ALREADY_RUNNING =
  'An optimization is already running for this scorecard. Wait for it to finish before starting another.'

/**
 * Optimize the prompt currently in the editor.
 *
 * ⚠ Measures the text in the EDITOR, not the saved criterion — it is saved as its own `prompts` row and the run
 * measures that. Nothing reaches production until the reviewer saves the scorecard.
 *
 * The click starts the run; there is no confirmation step. The sampler reports the call count as its first
 * milestone, Stop is always one click away, and too thin a sample is refused before the expensive stages.
 *
 * Reads and writes only THIS criterion's entry in the slice (keyed by `criteria.scid`), so any number of criteria
 * can show their own run at once and none can see another's.
 */
export const OptimizePromptButton = ({ values, criteria, savedPrompt, setFieldValue }) => {
  const dispatch = useDispatch()
  const criteriaScid = criteria?.scid
  const { scorecardConfig, settingsDirty } = useSelector((state) => state.qaCopilot)
  const { optimizationId, status, loading, error, failureReason } = useSelector((state) =>
    selectRun(state, criteriaScid)
  )
  // Runs dismissed in this session, by id. Consulted because clearing the entry re-arms recovery below and the
  // server's own record of the dismissal may not have landed yet.
  const dismissedIds = useSelector((state) => state.promptimizer?.dismissedIds ?? NO_DISMISSED)
  const loadedForConfigId = useRef(null)

  // A save moves the ground under a result, so re-ask the server rather than keeping our own copy. Whether a run
  // still belongs to the criterion is decided server-side: it does if the criterion's prompt row is untouched, it
  // doesn't if the save changed how the prompt runs. Clearing unconditionally gets the first case wrong; keeping
  // it gets the second wrong. So drop what we hold and let recovery re-ask.
  useEffect(() => {
    if (isNil(scorecardConfig?.id)) {
      return
    }

    if (!isNil(loadedForConfigId.current) && loadedForConfigId.current !== scorecardConfig.id) {
      dispatch(resetRun({ criteriaScid }))
    }

    loadedForConfigId.current = scorecardConfig.id
  }, [dispatch, scorecardConfig?.id, criteriaScid])

  // A run outlives the page. Without this, reloading mid-run leaves no panel while the job is still going, and the
  // next click 409s against the server's single-flight rule with nothing on screen explaining why.
  //
  // ⚠ Recovering an UNSAVED criterion does not work end to end: this component lives inside a criterion's form, so
  // after a reload there is no form for one to mount into. Restoring it has to happen at the scorecard level.
  useEffect(() => {
    if (isNil(scorecardConfig?.id)) {
      return
    }

    if (isNil(criteriaScid) && isNil(criteria?.id)) {
      return
    }

    // This criterion already has its run. Nothing another criterion does can reach this entry, so there is no
    // ownership to check — only whether we have already loaded ours.
    if (!isNil(optimizationId) || !isNil(status)) {
      return
    }

    const recover = async () => {
      const runs = await dispatch(fetchLatestRun({ scorecardConfigId: scorecardConfig.id }))
      // The newest run for this criterion, dismissed or not, then nothing if it was dismissed. Asking the server
      // to filter instead would turn "latest" into "latest undismissed", surfacing the run before it.
      const latest = runs.find((run) => matchesCriterion(run, criteria))

      if (isNil(latest) || latest.dismissed || dismissedIds.includes(latest.optimizationId)) {
        return
      }

      // Polled rather than just recorded: a finished run has to arrive with its candidates, its winner and the
      // prompt it started from, and one fetch supplies all of it.
      dispatch(
        pollOptimization({
          optimizationId: latest.optimizationId,
          criteriaScid,
          quiet: true,
        })
      )
    }

    recover()
  }, [dispatch, scorecardConfig?.id, criteria, criteriaScid, optimizationId, status, dismissedIds])

  useEffect(() => {
    if (isNil(optimizationId) || !POLLABLE_STATUSES.includes(status)) {
      return undefined
    }

    const timer = setInterval(() => {
      dispatch(pollOptimization({ optimizationId, criteriaScid, quiet: true }))
    }, POLL_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [dispatch, optimizationId, status, criteriaScid])

  /**
   * Put a finished result away. The run, its candidates and its metrics all stay; only the panel goes.
   *
   * Remembered by optimization id rather than as a flag on the criterion, so the next run here still shows.
   */
  const handleDismiss = () => {
    dispatch(dismissOptimization({ optimizationId, criteriaScid }))
  }

  // A run POSTs the editor's prompt as its own row, which needs a model like any other. The click is outside
  // Formik's submit, so the form's model rule never gates it — this does.
  const noModelSelected = !values.llm_model_id

  const handleClick = async () => {
    if (isEmpty(values.user_prompt) || !TRANSCRIPT_PLACEHOLDER.test(values.user_prompt)) {
      toast.error("Add a prompt that includes '$transcript' before optimizing")

      return
    }

    // A never-saved scorecard has no config, so there are no filters saying which calls this criterion covers.
    if (isNil(scorecardConfig?.id)) {
      toast.error('Save this scorecard first — its filters decide which calls we measure against')

      return
    }

    // The lineage is what a run is filed under, so without one there is nowhere to put the result. The form
    // generates one for every criterion including unsaved ones, so this is a broken invariant rather than a state
    // to design for — said out loud rather than leaving a button that appears to do nothing.
    if (isNil(criteriaScid)) {
      toast.error('This criterion has no id yet — reopen it and try again')

      return
    }

    // The panel goes up now, before either POST: starting a run is two sequential round trips, and a button that
    // sits inert for twenty seconds gets clicked again. The prompt is snapshotted in the same breath because
    // applying a candidate overwrites `user_prompt`, and a diff read live off the form would compare it to itself.
    dispatch(resetRun({ criteriaScid }))
    dispatch(
      setRun({
        criteriaScid,
        run: { status: RUN_STATUS.STARTING, originalPrompt: values.user_prompt },
      })
    )

    // Measure the criterion's own prompt record when the editor still holds it, so the run compares against
    // production's real answers instead of re-running the same text. See `canReuseCriterionPrompt`.
    const promptId = canReuseCriterionPrompt(values, savedPrompt, criteria)
      ? savedPrompt.id
      : await dispatch(
          savePromptForOptimization({
            criteriaScid,
            organizationId: scorecardConfig.organization_id,
            prompt: {
              user_prompt: values.user_prompt,
              system_prompt: values.system_prompt,
              first_x_minutes: values.first_x_minutes || null,
              last_x_minutes: values.last_x_minutes || null,
              side: values.side,
              include_timestamps: values.include_timestamps === 'true',
              silence_threshold_seconds: values.silence_threshold_seconds || null,
              llm_model_id: values.llm_model_id,
              max_response_tokens: values.max_response_tokens,
              feature: 'qa_copilot',
            },
          })
        )

    if (isNil(promptId)) {
      // `setStatus` rather than `resetRun`: the latter clears `error`, and the reason has to stay on screen.
      dispatch(setStatus({ criteriaScid, status: null }))

      return
    }

    const id = await dispatch(
      startOptimization({
        criteriaScid,
        promptId,
        scorecardConfigId: scorecardConfig.id,
        // Only buys anything when the text is unchanged, but always safe to send: the score pull filters on prompt
        // version too, so an edited prompt matches nothing and runs fresh.
        criterionId: criteria?.id ?? null,
        criterionDraft: {
          scid: criteriaScid,
          ssid: criteria?.ssid ?? null,
          name: values.name,
          description: values.description,
          criteria_type: values.criteria_type,
          weight: values.weight,
          must_be_present: values.must_be_present,
          scores_to_fallback: values.scores_to_fallback,
          fallback_initial_model_id: values.fallback_initial_model_id,
          llm_model_id: values.llm_model_id,
        },
      })
    )

    if (isNil(id)) {
      dispatch(setStatus({ criteriaScid, status: null }))

      return
    }

    // Poll once immediately so the panel shows something rather than an empty shell until the first tick.
    dispatch(pollOptimization({ optimizationId: id, criteriaScid, quiet: true }))
  }

  const running = ACTIVE_STATUSES.includes(status)
  const finished = status === RUN_STATUS.DONE
  const stopped = status === RUN_STATUS.FAILED || status === RUN_STATUS.CANCELLED

  return (
    <div className="copilot-optimize">
      {/* Every error kind renders, not a whitelist: an unhandled kind is a click that appears to do nothing.
          Unknown kinds fall through to the detail the server sent. */}
      {!isNil(error) && !running && (
        <ErrorMessage
          content={
            error.kind === 'alreadyRunning'
              ? ALREADY_RUNNING
              : error.detail || 'Something went wrong starting the optimization.'
          }
        />
      )}

      {stopped && (
        // The server records why a run failed, so this reports it rather than guessing. A cancel carries no reason
        // because it is a decision, not a fault.
        <ErrorMessage
          warning={status === RUN_STATUS.CANCELLED}
          content={
            status === RUN_STATUS.CANCELLED
              ? 'Optimization stopped.'
              : failureReason || 'That optimization did not finish, and did not say why.'
          }
        />
      )}

      {running && <OptimizeRunPanel criteriaScid={criteriaScid} />}

      {finished && (
        <OptimizeResult
          criteriaScid={criteriaScid}
          currentModelId={values.llm_model_id}
          setFieldValue={setFieldValue}
        />
      )}

      {/* Renders whenever a run is not in flight, including alongside a finished result — results survive a
          reload, so hiding this would leave no way to start another run. Placed after the result, which is the
          thing to read first when there is one. */}
      {!running && (
        <div className="copilot-optimize__intro">
          {!finished && <p>Optimize this prompt to increase its accuracy.</p>}

          {settingsDirty && (
            // Said before the click rather than in a dialog after it: a run samples against the SAVED scorecard,
            // which is what production scores.
            <ErrorMessage
              warning
              content="Measures against your saved scorecard eligibility settings, not any scorecard eligibility settings you haven't saved."
            />
          )}

          {usesFallback(values) && (
            // With fallback, production answers a call with a cheap model first and escalates, so its stored
            // answers are a two-model mix while every version we test runs on one. Said rather than modelled.
            <ErrorMessage
              warning
              content="This criterion uses fallback; the versions we test won't reflect that."
            />
          )}

          <div className="copilot-optimize__actions">
            <Button
              brand
              onClick={handleClick}
              loading={loading.start}
              disabled={noModelSelected}
              disabledTooltip="Select an AI model to optimize this prompt"
            >
              <IconWand /> {finished ? 'Optimize again' : 'Optimize'}
            </Button>

            {finished && (
              // Running again is what replaces a result, so this is only for putting one away without spending
              // anything. Durable (`spec.dismissed`), so a reload does not bring it back.
              <Button onClick={handleDismiss}>Dismiss this result</Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
